import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'energa_admin_token';
const PAGE_SIZE = 50;
const EXPORT_BATCH = 500;

const COLORS = {
  RED: '#C41E2A',
  GREEN: '#22C55E',
  WHITE: '#FFFFFF',
  GREY: '#6D6E71',
};

// ---- Helpers ----
const pad = (n) => String(n).padStart(2, '0');

const formatDateFR = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const todayFilename = () => {
  const d = new Date();
  return `energa-abonnes-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}.csv`;
};

const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (/[",\n\r]/.test(s)) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
};

// ---- Title bar (reused) ----
const TitleBar = () => (
  <div className="title-bar">
    <div className="title-dots">
      <span className="dot dot-red" />
      <span className="dot dot-yellow" />
      <span className="dot dot-green" />
    </div>
    <div className="title-text">ENERGA_ADMIN_v2.0</div>
    <div className="title-spacer" />
  </div>
);

// ---- LOGIN ----
function Login({ onAuth }) {
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!pwd || loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/subscribers`, {
        params: { limit: 1 },
        headers: { Authorization: `Bearer ${pwd}` },
      });
      if (res.status === 200) {
        localStorage.setItem(TOKEN_KEY, pwd);
        onAuth(pwd);
        return;
      }
      setError('> [ERROR] Mot de passe incorrect.');
    } catch (err) {
      const status = err && err.response ? err.response.status : 0;
      if (status === 401) {
        setError('> [ERROR] Mot de passe incorrect.');
      } else {
        setError('> [ERROR] Connexion impossible.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-page" data-testid="admin-login">
      <div className="terminal admin-terminal admin-terminal-small">
        <TitleBar />
        <div className="terminal-body admin-login-body">
          <div className="term-line">
            <span style={{ color: COLORS.RED }}>{'> ENERGA ADMIN PANEL'}</span>
          </div>
          <div className="term-line">
            <span style={{ color: COLORS.WHITE }}>{'> Authentification requise'}</span>
          </div>
          <div className="term-line">
            <span style={{ color: COLORS.RED }}>{"> Entrez le mot de passe d\u2019accès :"}</span>
          </div>
          <form onSubmit={submit} className="term-line input-line">
            <span style={{ color: COLORS.RED }}>{'> '}</span>
            <input
              ref={inputRef}
              type="password"
              className="admin-password-input"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              disabled={loading}
              autoComplete="current-password"
              aria-label="Mot de passe admin"
              data-testid="admin-password-input"
            />
            <span className="cursor-block" aria-hidden="true">█</span>
          </form>
          {error && (
            <div className="term-line" data-testid="admin-error">
              <span style={{ color: COLORS.RED }}>{error}</span>
            </div>
          )}
          {loading && (
            <div className="term-line">
              <span style={{ color: COLORS.GREY }}>{'> Vérification...'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- DASHBOARD ----
function Dashboard({ token, onLogout }) {
  const [page, setPage] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const fetchPage = useCallback(async (p) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/subscribers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: PAGE_SIZE, skip: p * PAGE_SIZE },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      if (err && err.response && err.response.status === 401) {
        onLogout();
        return;
      }
      setError('Erreur de chargement.');
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { fetchPage(page); }, [page, fetchPage]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const downloadCSV = async () => {
    if (exporting) return;
    setExporting(true);
    setError('');
    try {
      const all = [];
      let skip = 0;
      // Loop until we've drained the list
      while (true) {
        const res = await axios.get(`${API}/subscribers`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: EXPORT_BATCH, skip },
        });
        const batch = res.data.items || [];
        all.push(...batch);
        if (batch.length < EXPORT_BATCH) break;
        skip += EXPORT_BATCH;
      }
      const header = "Email,Date d'inscription,IP,User-Agent";
      const rows = all.map((it) => [
        csvEscape(it.email),
        csvEscape(it.created_at),
        csvEscape(it.ip),
        csvEscape(it.user_agent),
      ].join(','));
      const csv = '\uFEFF' + header + '\n' + rows.join('\n') + '\n';
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = todayFilename();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err && err.response && err.response.status === 401) {
        onLogout();
        return;
      }
      setError("Erreur lors de l'export CSV.");
    } finally {
      setExporting(false);
    }
  };

  const hasData = total > 0 || items.length > 0;

  return (
    <div className="admin-page" data-testid="admin-dashboard">
      <div className="terminal admin-terminal admin-terminal-large">
        <TitleBar />
        <div className="terminal-body admin-dashboard-body">
          <div className="term-line">
            <span style={{ color: COLORS.RED }}>{'> ENERGA ADMIN PANEL'}</span>
          </div>
          <div className="term-line">
            <span style={{ color: COLORS.GREEN }}>{'> Connecté ✓'}</span>
          </div>
          <div className="term-line">
            <span style={{ color: COLORS.WHITE }}>{`> Total d\u2019abonnés : ${total}`}</span>
          </div>

          <div className="admin-actions">
            <button
              type="button"
              onClick={downloadCSV}
              disabled={exporting || total === 0}
              className="admin-btn admin-btn-primary"
              data-testid="admin-csv-btn"
            >
              {exporting ? '[ ... GÉNÉRATION ... ]' : '[ TÉLÉCHARGER CSV ]'}
            </button>
            <button
              type="button"
              onClick={() => fetchPage(page)}
              disabled={loading}
              className="admin-btn admin-btn-secondary"
              data-testid="admin-refresh-btn"
            >
              {loading ? '[ CHARGEMENT... ]' : '[ ACTUALISER ]'}
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="admin-btn admin-btn-secondary"
              data-testid="admin-logout-btn"
            >
              [ DÉCONNEXION ]
            </button>
          </div>

          {error && (
            <div className="term-line">
              <span style={{ color: COLORS.RED }}>{`> ${error}`}</span>
            </div>
          )}

          {!hasData && !loading ? (
            <div className="term-line" data-testid="admin-empty">
              <span style={{ color: COLORS.GREY }}>{'> Aucun abonné pour le moment.'}</span>
            </div>
          ) : (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table" data-testid="admin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Email</th>
                      <th>Date d&rsquo;inscription</th>
                      <th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={`${it.email}-${i}`} data-testid="admin-row">
                        <td>{page * PAGE_SIZE + i + 1}</td>
                        <td>{it.email}</td>
                        <td>{formatDateFR(it.created_at)}</td>
                        <td>{it.ip || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="admin-pagination">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                  className="admin-btn admin-btn-secondary"
                  data-testid="admin-prev-btn"
                >
                  {'< PRÉCÉDENT'}
                </button>
                <span className="admin-page-indicator" data-testid="admin-page-indicator">
                  Page {page + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1 || loading}
                  className="admin-btn admin-btn-secondary"
                  data-testid="admin-next-btn"
                >
                  {'SUIVANT >'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Root ----
export default function Admin() {
  const [token, setToken] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) {
      setChecking(false);
      return undefined;
    }
    axios.get(`${API}/subscribers`, {
      params: { limit: 1 },
      headers: { Authorization: `Bearer ${stored}` },
    })
      .then(() => {
        if (!cancelled) setToken(stored);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => { cancelled = true; };
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (checking) {
    return (
      <div className="admin-page" data-testid="admin-checking">
        <div className="terminal admin-terminal admin-terminal-small">
          <TitleBar />
          <div className="terminal-body admin-login-body">
            <div className="term-line">
              <span style={{ color: COLORS.GREY }}>{'> Vérification de la session...'}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return token
    ? <Dashboard token={token} onLogout={logout} />
    : <Login onAuth={setToken} />;
}
